import { Component, Inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, UntypedFormControl, ValidationErrors, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { filter, take } from 'rxjs/operators';
import { PlannedConfigDialog } from 'src/app/components/oa/planned-downtime/planned-config-dialog/planned-config-dialog.component';
import { AdminService, ConfigItem } from '../../services/admin.service';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import { Subscription } from 'rxjs';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NgIf } from '@angular/common';
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

@Component({
    selector: 'app-oa-challenge-dialog',
    templateUrl: './oa-challenge-dialog.component.html',
    styleUrls: ['./oa-challenge-dialog.component.scss'],
    imports: [MatDialogTitle, MatIconButton, MatIcon, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatFormField, MatInput, NgIf, MatError, MatButton, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatDialogActions]
})
export class OaChallengeDialogComponent {
  objectid:string;
  site:string;
  environment:string;

  configData:ConfigItem[];

  helpEnabled:boolean=false;

  dataSource = new MatTableDataSource<any>();

  fgConfig: FormGroup;
   
  displayedColumns: string[] = ['StartDate','EndDate','Challenge', 'Actions']

  startDateGTendDate: boolean = false;
  startDateEQendDate: boolean = false;
  overlappingDates: boolean = false; 
  invalidFutureTimerangeExists: boolean = false;
  multipleEndDateBlank:boolean = false;

  valueChangesSubscription: Subscription = new Subscription;

  constructor(
    public dialogRef: MatDialogRef<PlannedConfigDialog>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public inData: any,
    private svcOAChallenge: AdminService
  ) { 
    this.objectid = this.inData.objectid;
    this.site = this.inData.site;
    this.environment = this.inData.environment;

    this.fgConfig = this.fb.group({
      rows: this.fb.array([])
    });

  }

  ngOnInit(): void {
    this.getConfig();    
  }

  getConfig() {
    this.svcOAChallenge.getOAChallengeConfig(this.site, this.environment, this.objectid).pipe(filter(out=>out != null), take(1)).subscribe(
      (out) => {
        if (out) {
          this.configData = out["Body"];
          console.log("LOADED CONFIG DATA!", this.configData);

          this.fgConfig = this.fb.group({
            rows: this.fb.array(this.configData.map( 
                (val:ConfigItem) => this.getFormGroup(val.id,                                            
                                                      dayjs(val.StartDate).format('MM/DD/YYYY'),
                                                      val.EndDate ? dayjs(val.EndDate).format('MM/DD/YYYY') : null, // Set empty string if EndDate is null
                                                      val.Challenge.toString()
                                                      )))
          });

          this.dataSource = new MatTableDataSource((this.fgConfig.get('rows') as FormArray).controls);

          this.valueChangesSubscription = this.configRows.valueChanges.subscribe(() => {
            this.startDateGTendDate = false;
            this.startDateEQendDate = false;
            this.overlappingDates = false;
            this.invalidFutureTimerangeExists = false;
            this.multipleEndDateBlank = false;
          });
        }
      }
    );
  }

  getFormGroup(id:number, StartDate:string, EndDate:string, Challenge:string, isEditing:boolean=false):FormGroup {
    return this.fb.group({
            id: new UntypedFormControl(id),            
            StartDate: new UntypedFormControl({value: StartDate, disabled: false},[this.validatorDate(), Validators.required]),
            EndDate: new UntypedFormControl({value: EndDate, disabled: false},[this.validatorDate()]),
            Challenge: new UntypedFormControl({value: Challenge, disabled: false}, [this.numericValidator, Validators.required]),
            isEditing: new FormControl(isEditing),
            isDeleted: new FormControl(false)
          });
  }

  validatorDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let inDate: string = control.value;
      if (inDate && !(dayjs(inDate, 'MM/DD/YYYY', true).isValid())) 
        {
        return { notValidDate: true };
      }
  
      return null;
    } 
  }

  numericValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return isNaN(value) ? { numeric: true } : null;
  }

  get configRows(): FormArray {
    return this.fgConfig.get('rows') as FormArray;
  }

  formatDate(control:AbstractControl) {
    if (!control.invalid) {
      let dateValue:string = control.value;
      let dateFormatted:string = "";

      if (dateValue.trim() != "") {    
        dateFormatted = dayjs(dateValue).format('MM/DD/YYYY').toString();
      }
      control.patchValue(dateFormatted);
    }
  }

  add(inForm) {
    let newRow: FormGroup = this.getFormGroup(0, "", "", "", true);
    newRow.markAllAsTouched();
    inForm.get('rows').push(newRow);

    this.dataSource = new MatTableDataSource((inForm.get('rows') as FormArray).controls);
  }

  editLine(inForm, idx:number) {
    inForm.get('rows').at(idx).get('isEditing').patchValue(true);
  }

  delete(inForm, idx:number):void {
    if (inForm.get('rows').at(idx).get('id').value != 0) {
      inForm.get('rows').at(idx).get('isDeleted').patchValue(true);
      this.fgConfig.markAsDirty();
    } else {
      inForm.get('rows').removeAt(idx);

      this.dataSource = new MatTableDataSource((inForm.get('rows') as FormArray).controls);
    }

  }

  undo(inForm, idx:number) {
    inForm.get('rows').at(idx).get('isEditing').patchValue(false);
    inForm.get('rows').at(idx).get('isDeleted').patchValue(false);
    inForm.get('rows').at(idx).get('StartDate').patchValue(dayjs(this.configData[idx]["StartDate"]).format('MM/DD/YYYY'));
    inForm.get('rows').at(idx).get('EndDate').patchValue(this.configData[idx]["EndDate"] ? dayjs(this.configData[idx]["EndDate"]).format('MM/DD/YYYY') : null);
    inForm.get('rows').at(idx).get('Challenge').patchValue(this.configData[idx]["Challenge"]);
    inForm.get('rows').at(idx).markAsPristine();
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  hasOverlappingDates() {
    this.startDateGTendDate = false; 
    this.startDateEQendDate = false; 
    this.overlappingDates = false;
    this.invalidFutureTimerangeExists = false;
    this.multipleEndDateBlank=false;

    const rows = this.fgConfig.get('rows').value;

    for (let i = 0; i < rows.length; i++) {
      const currentEndDate = dayjs(rows[i].EndDate, 'MM/DD/YYYY');
      const currentStartDate = dayjs(rows[i].StartDate, 'MM/DD/YYYY');      

      for (let j = 0; j < rows.length; j++) {
        if (i !== j) {
          const otherStartDate = dayjs(rows[j].StartDate, 'MM/DD/YYYY');
          const otherEndDate = dayjs(rows[j].EndDate, 'MM/DD/YYYY');          
          
          // Check if either StartDate or EndDate is same as other StartDate or EndDate
          if (currentStartDate.isSame(otherStartDate) || currentEndDate.isSame(otherEndDate)
               ||  currentEndDate.isSame(otherStartDate) || currentStartDate.isSame(otherEndDate)){
              this.startDateEQendDate = true;             
              }

          // Check if StartDate grater than EndDate
          else if (currentStartDate.isAfter(currentEndDate)) {
              this.startDateGTendDate = true;
            }
          
          // StartDate is between another record StartDate and EndDate
          // EndDate is between another record StartDate and EndDate
          else if (currentStartDate.isBetween(otherStartDate, otherEndDate) || currentEndDate.isBetween(otherStartDate, otherEndDate)) {
            this.overlappingDates = true;

          }
          // Multiple EndDates are Blank
          else if ((!currentEndDate || !dayjs(currentEndDate).isValid() ) && (!otherEndDate || !dayjs(otherEndDate).isValid() )){              
            this.multipleEndDateBlank = true;                     
          }

          //When the Current EndDate is empty verify any future record exists which is invalid, and needs to be fixed.
          else if ((!currentEndDate || !dayjs(currentEndDate).isValid() ) && (otherEndDate.isAfter(currentStartDate) || otherStartDate.isAfter(currentStartDate))){
            this.invalidFutureTimerangeExists = true;                      
          }

        }
      }
    }
  }

  isDateInPast(startDate: string, endDate?: string): boolean {
    const today = dayjs().startOf('day');
    const start = dayjs(startDate, 'MM/DD/YYYY');
    const end = dayjs(endDate, 'MM/DD/YYYY');
    if (endDate !== undefined) {
      return (start.isValid() && start.isBefore(today)) && (end.isValid() && end.isBefore(today))
    }
    else{
      return start.isValid() && start.isBefore(today)
    }
  }

  save() {

    this.hasOverlappingDates();

    if (!(this.startDateEQendDate || this.startDateGTendDate || this.overlappingDates || this.invalidFutureTimerangeExists || this.multipleEndDateBlank)) {
      // Return an array with all the times converted from strings to number of seconds
      let adjustedItems:ConfigItem[] = this.fgConfig.get('rows').value.map( (item) => {
        let configItemUpdate:ConfigItem = {
          id: item.id,
          StartDate: dayjs(item.StartDate).format('YYYY-MM-DD').toString(),        
          EndDate: item.EndDate ? dayjs(item.EndDate).format('YYYY-MM-DD').toString() : null, // Handle empty EndDate,
          Challenge: item.Challenge,
          Active: item.isDeleted ? 0 : 1
          
        }
        console.log("configItemUpdate", configItemUpdate);
    
        return configItemUpdate;

      });
      
      // Get filtered array of items that have changed, this includes deletes
      let changedItems:ConfigItem[] = adjustedItems.filter( 
        (item, index) => {
          // Check if record has been marked for delete or a change has been made
          if (
              (item.Active == 0 ||
                item.id == 0 ||               
                this.configData[index].StartDate != item.StartDate ||
                this.configData[index].EndDate != item.EndDate ||
                this.configData[index].Challenge != item.Challenge
              )) {      
              return true; // Record is add, update, or delete
          } else {
            return false; // Record hasn't changed don't include
          }
        });

      if (changedItems.length > 0) {
        this.svcOAChallenge.saveOAChallengeConfig(this.site, this.environment, this.objectid, changedItems).subscribe(
          (out) => {
            this.close(true);
          }
        );
      } else {
        this.close(false);
      }
    }
  }

  close(updates:boolean = false) {
    this.dialogRef.close({
      updates: updates
    });
  }
}
